<template>
  <template v-if="isAppListLoading">
    <PrmSkeleton
      v-for="item in 6"
      :key="item"
      width="36px"
      height="36px"
    />
  </template>
  <template v-else>
    <ButtonNav
      :label="i18n.global.t('page.home.title')"
      :icon="HomeIcon"
      :route-to="{
        name: ERouteName.HOME,
      }"
      :disabled="appStore.isNavigating"
    />
    <ButtonNav
      :label="i18n.global.t('page.appOverview.title')"
      :icon="AppsIcon"
      :route-to="{
        name: ERouteName.APP_OVERVIEW,
      }"
      :disabled="appStore.isNavigating"
    />
    <SeparatorAppNavbar />
    <PrmSkeleton
      v-if="isCustomPinnedAppListLoading"
      width="36px"
      height="36px"
    />
    <ButtonNavbarApp
      v-for="appId in pinnedActiveApps"
      v-else
      :key="appId"
      :app-id="appId"
    />
    <SeparatorAppNavbar v-if="assetId" />
    <MenuAssetAppNavigation
      v-if="assetId"
      :asset-id="assetId"
    />
    <div
      class="flex-column"
      style="margin-top: auto; position: sticky; bottom: 0;"
    >
      <ButtonNav
        v-if="isPlaygroundEnabled()"
        icon="pi pi-directions"
        label="Playground"
        :route-to="({
          name: ERouteName.PLAYGROUND,
        } as unknown as RouteLocationRaw)"
        :disabled="appStore.isNavigating"
      />
      <ButtonNav
        v-if="isDevEnvironment()"
        icon="pi pi-hammer"
        label="Debugging Tools"
        :route-to="({
          name: ERouteName.DEBUG_TOOLS,
        } as unknown as RouteLocationRaw)"
        :disabled="appStore.isNavigating"
      />
      <a
        v-if="!appStore.isNavigating"

        target="_blank"
        :href="userManualUrl"
        :title="i18n.global.t('page.userManual.title')"
      >
        <PrmButton
          size="small"
          text
          type="button"
          severity="secondary"
          style="color: var(--text-color);"
        >
          <template #icon>
            <i class="pi pi-question-circle" />
          </template>
        </PrmButton>
      </a>
    </div>
  </template>
</template>

<script setup lang="ts">
import PrmButton from '@NitrexGroup/primevue/button';
import PrmSkeleton from '@NitrexGroup/primevue/skeleton';
import {
  i18n,
} from '@i18n';
import ButtonNav from '@qmulus/ui/components/app-navbar/button-nav.vue';
import SeparatorAppNavbar from '@qmulus/ui/components/app-navbar/separator-app-navbar.vue';
import {
  computed,
} from 'vue';
import {
  RouteLocationRaw,
  useRoute,
} from 'vue-router';

import AppsIcon from '@/assets/icon/apps.svg?component';
import HomeIcon from '@/assets/icon/home.svg?component';
import ButtonNavbarApp from '@/components/button-navbar-app/button-navbar-app.vue';
import MenuAssetAppNavigation
  from '@/components/menu-asset-app-navigation/menu-asset-app-navigation.vue';
import config from '@/config/config.ts';
import {
  isPlaygroundEnabled,
} from '@/playground/playground.ts';
import {
  ERouteName,
} from '@/router/route-name.ts';
import {
  useAppStore,
} from '@/store/pinia/app.ts';
import {
  useCustomUserStorageStore,
} from '@/store/pinia/custom-user-storage.ts';
import {
  EAppId,
} from '@/types/app-type.ts';
import {
  isDevEnvironment,
} from '@/utils/environment.js';
import {
  getUserManualUrl,
} from '@/utils/user-manual.ts';

const appStore = useAppStore();
const customUserStorage = useCustomUserStorageStore();
const route = useRoute();
customUserStorage.loadCustomPinnedAppList();
appStore.loadAppList();

const isAppListLoading = computed(() => appStore.appListIsLoading);
const isCustomPinnedAppListLoading = computed(() => customUserStorage.customPinnedAppListIsLoading);

const assetId = computed<number | undefined>(() => {
  if ((route.params as any).assetId) {
    return parseInt((route.params as any).assetId as string, 10);
  }
  // if only one assetId is selected from the filters, that is clear and we can offer
  // to provide contextual navigation for that one asset.
  if (route.query.assetIdList && route.query.assetIdList.length && route.query.assetIdList.length === 1) {
    return parseInt(route.query.assetIdList[0] as string, 10);
  }
  if (route.query.assetIds && typeof route.query.assetIds === 'string') {
    const assetIds = route.query.assetIds.split(',');
    return parseInt(assetIds[0], 10);
  }
  return undefined;
});

const pinnedActiveApps = computed<EAppId[]>(() => {
  const activeAppIds: number[] = (appStore.appList || []).map((app) => app.appId);
  return (customUserStorage.customPinnedAppList || [])
    .filter((appId) => activeAppIds.includes(appId));
});

const userManualUrl = computed(() => getUserManualUrl(
  config.VITE_APP_USER_MANUAL_URL,
  i18n.global.locale.value,
  route.meta.userManualPage,
));
</script>

<style>
.navbar-icon__image {
  min-width: 1.5rem;
  width: 1.5rem;
  height: 1.5rem;
}
</style>
